import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { QUESTIONS, pickTake } from '../config/builtQuiz';
import { BUILT_STORAGE_KEY } from '../config/builtSessions';
import { QuizResult } from './BuiltQuizResult';
import './BuiltQuiz.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUIZ_KEY = 'built_quiz';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const INITIAL = { step: 0, answers: {}, want: [], phase: 'q', take: null };

const load = () => {
  try { return { ...INITIAL, ...JSON.parse(sessionStorage.getItem(QUIZ_KEY) || '{}') }; } catch (e) { return INITIAL; }
};

const ContactForm = ({ onDone }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (!firstName.trim() || !EMAIL_RE.test(email.trim())) { setError('need your first name & email for this part'); return; }
    onDone({ first_name: firstName.trim(), email: email.trim() });
  };
  return (
    <form className="qz-contact" onSubmit={submit} noValidate data-testid="quiz-contact-form">
      <h3 className="qz-q">where should i send my take?</h3>
      <input className="qz-input" placeholder="first name" aria-label="first name" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="quiz-first-name" />
      <input className="qz-input" type="email" placeholder="email" aria-label="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="quiz-email" />
      {error && <p className="qz-error" role="alert" data-testid="quiz-contact-error">{error}</p>}
      <button type="submit" className="qz-next" data-testid="quiz-contact-submit">show me →</button>
    </form>
  );
};

const Question = ({ index, state, onPick, onToggle, onNext }) => {
  const item = QUESTIONS[index];
  const selected = item.multi ? state.want : [state.answers[item.key]];
  return (
    <div className="qz-question" key={item.key} data-testid={`quiz-question-${index + 1}`}>
      <h3 className="qz-q">{item.q}</h3>
      <div className="qz-options">
        {item.options.map(([value, label]) => (
          <button
            key={value} type="button"
            className={`qz-opt${selected.includes(value) ? ' is-on' : ''}`}
            aria-pressed={selected.includes(value)}
            onClick={() => (item.multi ? onToggle(value) : onPick(item.key, value))}
            data-testid={`quiz-q${index + 1}-${value}`}
          >
            {label}
          </button>
        ))}
      </div>
      {item.multi && (
        <button type="button" className="qz-next" disabled={state.want.length === 0} onClick={onNext} data-testid="quiz-next">next →</button>
      )}
    </div>
  );
};

export const BuiltQuiz = ({ signup }) => {
  const [state, setState] = useState(load);
  const cardRef = useRef(null);
  const first = useRef(true);

  useEffect(() => { sessionStorage.setItem(QUIZ_KEY, JSON.stringify(state)); }, [state]);

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const el = cardRef.current;
    if (el && (state.phase === 'result' || el.getBoundingClientRect().top < 0)) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [state.step, state.phase]);

  const finish = (answers, want, contact, direct) => {
    const take = pickTake(answers);
    axios.post(`${API}/built/quiz`, {
      first_name: contact.first_name, email: contact.email, direct, take, ...answers, want,
    }).catch(() => {});
    setState((s) => ({ ...s, phase: 'result', take }));
  };

  const onPick = (key, value) => setState((s) => ({ ...s, answers: { ...s.answers, [key]: value }, step: s.step + 1 }));
  const onToggle = (value) => setState((s) => ({
    ...s, want: s.want.includes(value) ? s.want.filter((w) => w !== value) : [...s.want, value],
  }));
  const onNext = () => {
    if (signup?.email) finish(state.answers, state.want, signup, false);
    else setState((s) => ({ ...s, phase: 'email' }));
  };
  const onBack = () => setState((s) => (s.phase === 'email' ? { ...s, phase: 'q' } : { ...s, step: Math.max(0, s.step - 1) }));

  const total = QUESTIONS.length;
  const progress = state.phase === 'q' ? (state.step / total) * 100 : 100;

  return (
    <div className="qz-card" ref={cardRef} data-testid="built-quiz">
      {state.phase === 'result' ? (
        <QuizResult take={state.take} answers={state.answers} want={state.want} />
      ) : (
        <>
          <div className="qz-top">
            {(state.step > 0 || state.phase === 'email') ? (
              <button type="button" className="qz-back" onClick={onBack} data-testid="quiz-back">← back</button>
            ) : <span />}
          </div>
          <div className="qz-bar" aria-hidden="true"><span style={{ width: `${progress}%` }} /></div>
          {state.phase === 'q' && (
            <Question index={Math.min(state.step, total - 1)} state={state} onPick={onPick} onToggle={onToggle} onNext={onNext} />
          )}
          {state.phase === 'email' && <ContactForm onDone={(c) => finish(state.answers, state.want, c, true)} />}
        </>
      )}
    </div>
  );
};
